/**
 * Client-side recovery from an expired Auth0 session.
 *
 * When the Auth0 session cookie expires, `withApiAuthRequired` answers every
 * wevr API route with `401 {error: 'not_authenticated'}`. Before this module
 * existed each caller surfaced that as a raw error toast — "GraphQL Error
 * (Code: 401): {...}" — while the page kept rendering cached data, so the user
 * had no cue their session was gone and retried the same action in a loop.
 *
 * The pieces here are deliberately split so the transports (`apiFetch`, the
 * GraphQL client wrapper) share one definition of "the session is gone" and one
 * latch, rather than each deciding for itself.
 */

export const LOGIN_PATH = '/api/auth/login';

/** The `error` value `withApiAuthRequired` puts in its 401 body. */
export const NOT_AUTHENTICATED = 'not_authenticated';

export const SESSION_EXPIRED_MESSAGE =
  'Your session has expired. Please log in again.';

/**
 * Replaces the transport's own error once we know the cause is an expired
 * session. Call sites that do `notification('error', error.message)` then show
 * this sentence instead of a JSON blob, which is the visible half of the bug.
 */
export class SessionExpiredError extends Error {
  constructor(message: string = SESSION_EXPIRED_MESSAGE) {
    super(message);
    this.name = 'SessionExpiredError';
    // Extending a built-in through the TS/babel downlevel loses the prototype
    // chain, which would break `instanceof` for every caller.
    Object.setPrototypeOf(this, SessionExpiredError.prototype);
  }
}

const isBrowser = (): boolean => typeof window !== 'undefined';

const hasNotAuthenticatedMarker = (body: unknown): boolean => {
  if (typeof body === 'string') {
    return body.includes(NOT_AUTHENTICATED);
  }
  if (body !== null && typeof body === 'object') {
    const { error } = body as { error?: unknown };
    return typeof error === 'string' && error.includes(NOT_AUTHENTICATED);
  }
  return false;
};

/**
 * Decides whether a 401 means "the Auth0 session is gone".
 *
 * Every wevr API route the browser calls is wrapped in `withApiAuthRequired`,
 * whose only 401 carries `error: 'not_authenticated'`. `/api/graphql` proxies
 * Hasura but always answers 200 (or 500) itself, so a 401 arriving at the
 * browser can only have come from that wrapper.
 *
 * The marker is therefore expected, and a body that carries a *different*
 * `error` is left alone — a future route answering 401 for "you lack this
 * permission" should not claim the session expired. A 401 we could not read a
 * body from is still treated as an expiry: regressing to the raw-error toast
 * this module exists to remove is the worse failure.
 */
export const isNotAuthenticatedStatus = (
  status: unknown,
  body?: unknown
): boolean => {
  if (status !== 401) {
    return false;
  }
  if (body === undefined || body === null) {
    return true;
  }
  if (typeof body === 'object' && !('error' in (body as object))) {
    return true;
  }
  return hasNotAuthenticatedMarker(body);
};

type ClientErrorLike = { response?: unknown };

/**
 * Recognises the expired session in whatever `graphql-request` threw.
 *
 * On a non-2xx it raises `ClientError` built as
 * `{...parsedBody, status, headers}` (graphql-request/dist/index.js), so the
 * 401 body's own `error` key sits directly on `error.response` alongside
 * `status` — one object serves as both status carrier and body.
 */
export const isNotAuthenticatedError = (error: unknown): boolean => {
  if (error instanceof SessionExpiredError) {
    return true;
  }
  const response = (error as ClientErrorLike)?.response;
  if (response === null || typeof response !== 'object') {
    return false;
  }
  return isNotAuthenticatedStatus(
    (response as { status?: unknown }).status,
    response
  );
};

let expired = false;
const listeners = new Set<() => void>();

export const isSessionExpired = (): boolean => expired;

/**
 * Flips the latch and wakes the modal. Returns whether this call was the one
 * that flipped it, so a caller can log the first detection without logging the
 * dozen concurrent requests that fail right behind it.
 *
 * Deliberately inert on the server: this is module-level state, and a Next.js
 * server process shares module state across every request it handles. Latching
 * there would mark one user's expiry as everybody's.
 */
export const markSessionExpired = (): boolean => {
  if (!isBrowser() || expired) {
    return false;
  }
  expired = true;
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      // A subscriber that throws must not stop the remaining subscribers from
      // learning the session is gone.
      console.error('Session expiry listener failed', error);
    }
  });
  return true;
};

export const subscribeToSessionExpiry = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Test seam — production only ever moves the latch in one direction. */
export const resetSessionExpiry = (): void => {
  expired = false;
  listeners.clear();
};

/**
 * `returnTo` is echoed back as a redirect target after login, so it has to stay
 * same-origin. Anything that is not a single-slash absolute path — a full URL,
 * a protocol-relative `//evil.example`, or the `/\` some browsers resolve the
 * same way — collapses to the site root.
 */
const toSameOriginPath = (value: unknown): string => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return '/';
  }
  if (value.startsWith('//') || value.startsWith('/\\')) {
    return '/';
  }
  return value;
};

export const buildLoginUrl = (returnTo: unknown): string =>
  `${LOGIN_PATH}?returnTo=${encodeURIComponent(toSameOriginPath(returnTo))}`;

/** Where the user should land once they have logged back in. */
export const currentReturnTo = (): string =>
  isBrowser() ? `${window.location.pathname}${window.location.search}` : '/';

export const redirectToLogin = (): void => {
  if (!isBrowser()) {
    return;
  }
  window.location.assign(buildLoginUrl(currentReturnTo()));
};
